import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  PUSH_SUPPORTED,
  getPushState,
  enablePush,
  disablePush,
  sendTestPush,
} from "../lib/push";

/**
 * Small bento card that lets an agent enable browser push notifications for
 * new customer messages on leads assigned to them (or any unassigned lead in
 * their department).
 *
 * Lives in Settings and is visible to every logged-in user.
 */
export default function PushNotificationsCard() {
  const [state, setState] = useState({ supported: PUSH_SUPPORTED, permission: "default", subscribed: false });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    const s = await getPushState();
    setState(s);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push notifications enabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Push notifications disabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Could not disable notifications");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await sendTestPush();
      if (res.sent > 0) {
        toast.success(`Test push sent to ${res.sent} device${res.sent > 1 ? "s" : ""}`);
      } else {
        toast.warning("No active subscriptions found on this account");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test push");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bento-card" data-testid="push-notifications-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-[#15803D]/10 rounded-md">
          <Bell size={20} className="text-[#15803D]" />
        </div>
        <h2
          className="text-xl font-medium text-[#1C1917]"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          Push Notifications
        </h2>
      </div>

      {!state.supported ? (
        <div className="p-3 rounded-md bg-[#FEF2F2] text-sm text-[#991B1B]" data-testid="push-unsupported">
          This browser does not support web push notifications. Try Chrome, Edge, or Firefox on desktop / Android.
        </div>
      ) : state.permission === "denied" ? (
        <div className="p-3 rounded-md bg-[#FEF2F2] text-sm text-[#991B1B] space-y-2" data-testid="push-blocked">
          <p className="font-medium">Notifications are blocked for this site.</p>
          <p>
            Click the lock icon in the address bar → <strong>Notifications</strong> → <strong>Allow</strong>, then
            reload.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-[#57534E]" data-testid="push-description">
            Get an instant alert on this device when a customer sends you a new WhatsApp message — even when the CRM tab is in the background.
          </p>

          <div className="flex items-center justify-between p-3 bg-[#F5F2EA] rounded-md">
            <div>
              <p className="text-sm font-medium text-[#1C1917]">Status</p>
              <p className="text-xs text-[#57534E] mt-0.5" data-testid="push-status-text">
                {state.subscribed ? "Subscribed on this device" : "Not subscribed on this device"}
              </p>
            </div>
            {state.subscribed ? (
              <button
                onClick={handleDisable}
                disabled={busy}
                data-testid="push-disable-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-[#BC4B35] text-white hover:bg-[#9A3D2A] disabled:opacity-60 transition-colors"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <BellOff size={14} />}
                Turn off
              </button>
            ) : (
              <button
                onClick={handleEnable}
                disabled={busy}
                data-testid="push-enable-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-[#15803D] text-white hover:bg-[#126B33] disabled:opacity-60 transition-colors"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
                Turn on
              </button>
            )}
          </div>

          {state.subscribed ? (
            <button
              onClick={handleTest}
              disabled={testing}
              data-testid="push-send-test-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border border-[#E7E5E4] text-[#312E81] hover:bg-[#312E81]/5 disabled:opacity-60 transition-colors"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send test notification
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
